<script setup>
import InputError from '@/components/InputError.vue';
import Button from '@/components/ui/button/Button.vue';
import { Form, FormControl, FormField, FormLabel } from '@/components/ui/form';
import FormItem from '@/components/ui/form/FormItem.vue';
import Input from '@/components/ui/input/Input.vue';
import { Separator } from '@/components/ui/separator';
import PartnerAuthenticateLayout from '@/Layouts/PartnerAuthenticateLayout.vue';
import { Head, Link, useForm } from '@inertiajs/vue3';
import { Eye, EyeOff, FishIcon } from 'lucide-vue-next';
import { onMounted, ref } from 'vue';
import { route } from 'ziggy-js';

defineProps({
    canResetPassword: {
        type: Boolean,
    },
    status: {
        type: String,
    },
});

const showPassword = ref(false);

const form = useForm({
    email: '',
    password: '',
    remember: false,
});

const submit = () => {
    form.post(route('partner.login'), {
        onFinish: () => {
            form.reset('password');
        },
        onError: (errors) => {
            let reason = '';

            // Hata mesajına göre uygun parametreyi seç
            if (errors.email === 'Hesabınız 30 gündür giriş yapmadığınız için bloke edilmiştir.') {
                reason = 'inactive'; // 30 gün giriş yapmayanlar için
            } else if (errors.email === 'Hesabınız 5 kez yanlış giriş yapıldığı için bloke edilmiştir.') {
                reason = 'failed_attempts'; // 5 kez hatalı giriş yapanlar için
            }

            // Parametreyi URL'ye ekleyerek yönlendirme
            if (reason) {
                window.location.href = route('user.block', { reason: reason });
            }
        },
    });
};

const lowerToTurkish = () => {
    form.email = form.email.replace(/İ/g, 'i').toLowerCase();
};

const isSakuraDomain = ref(false);
const organizationName = ref('Sakura Klima');

onMounted(() => {
    isSakuraDomain.value = window.location.hostname.includes('sakuraklima.com');
    console.log('isSakuraDomain:', isSakuraDomain.value);
    if (isSakuraDomain.value) {
        organizationName.value = 'Sakura';
    } else {
        organizationName.value = 'Netisa';
    }
});
</script>

<template>
    <PartnerAuthenticateLayout>
        <Head title="Giriş yap" />
        <section class="">
            <div class="flex flex-col space-y-2">
                <h1 class="text-2xl font-semibold tracking-tight lg:text-3xl">
                    {{ organizationName }}'ya Hoşgeldiniz...
                </h1>
                <p class="text-[13px] text-gray-500">
                    Devam etmek için lütfen kayıtlı e-posta adresinizi ve parolanızı giriniz.
                </p>
            </div>
            <Form class="mt-5 space-y-2" @submit="submit">
                <FormField name="name">
                    <FormItem>
                        <FormLabel :required="true">E-posta Adresi</FormLabel>
                        <FormControl>
                            <Input
                                type="email"
                                id="email"
                                placeholder="E-posta Adresi"
                                v-model="form.email"
                                required
                                autofocus
                                autocomplete="email"
                                @blur="lowerToTurkish"
                            />
                        </FormControl>
                        <InputError class="mt-2" :message="form.errors.email" />
                    </FormItem>
                    <FormItem>
                        <FormLabel :required="true">Parola</FormLabel>
                        <FormControl>
                            <div class="relative">
                                <Input
                                    :type="showPassword ? 'text' : 'password'"
                                    id="password"
                                    placeholder="Parola"
                                    v-model="form.password"
                                    required
                                    autocomplete="current-password"
                                />
                                <span class="absolute right-0 top-0 flex size-10 items-center justify-center">
                                    <button
                                        type="button"
                                        @click="showPassword = !showPassword"
                                        :aria-label="showPassword ? 'Parolayı gizle' : 'Parolayı göster'"
                                        class="text-muted-foreground hover:text-primary"
                                    >
                                        <EyeOff size="18" v-if="showPassword" />
                                        <Eye size="18" v-else />
                                    </button>
                                </span>
                            </div>
                        </FormControl>
                        <InputError class="mt-2" :message="form.errors.password" />
                    </FormItem>
                </FormField>
                <div class="space-y-4">
                    <Button authenticatebutton :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                        Giriş Yap
                    </Button>
                    <Link
                        v-if="canResetPassword"
                        :href="route('partner.password.request')"
                        class="mt-5 flex items-center justify-center"
                    >
                        <FishIcon />
                        <div class="ms-2 text-sm font-medium">Parolamı Unuttum</div>
                    </Link>
                    <Separator class="my-6" label="YA DA" />
                    <div class="flex flex-col items-center justify-center">
                        <div class="text-sm font-semibold text-gray-400">Henüz kayıtlı değil misin?</div>
                        <Link v-if="canResetPassword" :href="route('partner.register')" class="mt-2">
                            <Button authenticatebutton="">Kayıt Ol</Button>
                        </Link>
                    </div>
                </div>
            </Form>
        </section>
    </PartnerAuthenticateLayout>
</template>
